import {
  assertIsUUID,
  isUUID,
  type UUID,
} from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type BasicData,
  type CreateObjectPayload,
  type CreateRelationPayload,
  type CreateViewPayload,
  type DiagramPayload,
  type DraftData,
  type GraphicData,
  type PayloadDraftEntry,
  type PublishViewPayload,
  relationsTypeArray,
} from '@bizzdesign/modeler/models';
import { BDCoreMetaModelDefinition } from '@bizzdesign/sdk-knowledgegraph/bdcore';
import type { DraftObjectsPayload } from '@bizzdesign/shared/models';
import { convertDraftAttributeCategoryPayloadToAttributeCategoryValuesPayload } from '@bizzdesign/shared/utils';
import {
  getPayloadBasedOnElementKind,
  populateGraphicNameInPayload,
  splitPayloadIntoBaseAndTargetElements,
} from '../common/payload.utils';
import {
  convertTypeNameToRelationRoleName,
  convertTypeNameToSdkElementType,
} from './sdk-types.utils';

export const VIEW_PUBLISHING_ERROR_LOG = 'view publishing failed';

function extractImageIdsFromGraphics(graphics: PayloadDraftEntry): UUID[] {
  const imageIds = new Set<UUID>();
  for (const element of Object.values(graphics)) {
    const style = (element as GraphicData).style as {
      imageMetadata?: { imageId?: string };
    } | null;
    const imageId = style?.imageMetadata?.imageId;
    if (imageId && isUUID(imageId)) {
      imageIds.add(imageId);
    }
  }
  return [...imageIds];
}

function splitTargetElementReferenceIds(targetElements: PayloadDraftEntry): {
  referencedObjectIds: UUID[];
  referencedRelationIds: UUID[];
} {
  const referencedObjectIds: UUID[] = [];
  const referencedRelationIds: UUID[] = [];
  for (const id of Object.keys(targetElements)) {
    assertIsUUID(id);
    const model = targetElements[id] as DraftData;
    if (!!model.sourceId && relationsTypeArray.includes(model.metaModelTerm)) {
      referencedRelationIds.push(id);
    } else {
      referencedObjectIds.push(id);
    }
  }
  return { referencedObjectIds, referencedRelationIds };
}

/**
 * Converts the draft payload data into publish payload data for SDK.
 * It extracts the graphical elements since they will be sent as-is.
 * Converts the types of semantical elements to match SDK types. All new objects are stored in the
 * objectCreationPayload while all new relations are stored in relationCreationPayload.
 * Referenced semantic ids are split into `referencedObjectIds` and `referencedRelationIds`.
 * @param draft payload object. Its keys are IDs of semantical/graphical elements
 * @param viewName name of the draft view
 * @param viewId UUID of the draft view
 * @param draftObjectsPayload payload containing draft objects with attributes
 * is being published
 * @returns publishing payload for SDK
 */
export function parseDraftToPublishPayload(
  draft: DiagramPayload,
  viewName: string,
  viewId: string,
  draftObjectsPayload: DraftObjectsPayload | undefined,
  viewDescription: string | undefined,
): PublishViewPayload {
  const draftPayload = draft.payload as unknown as PayloadDraftEntry;
  const viewGraphicContent = getPayloadBasedOnElementKind(
    draftPayload,
    'diagram',
  );
  const viewSemanticContent = getPayloadBasedOnElementKind(
    draftPayload,
    'model',
  );

  const updatedViewGraphicContent = populateGraphicNameInPayload(
    viewGraphicContent,
    viewSemanticContent,
  );

  let updatedDraftObjectsPayload: DraftObjectsPayload = {};

  if (draftObjectsPayload) {
    updatedDraftObjectsPayload = Object.entries(
      updatedViewGraphicContent,
    ).reduce((acc: DraftObjectsPayload, [id, draftData]) => {
      const semanticId = (draftData as GraphicData).semanticsId;

      if (draftObjectsPayload[id] && semanticId) {
        acc[semanticId] = draftObjectsPayload[id];
      }
      return acc;
    }, {});
  }

  const { baseElements, targetElements } =
    splitPayloadIntoBaseAndTargetElements(viewSemanticContent);

  const fromRepoElements = draft.fromRepoElements;
  const { referencedObjectIds, referencedRelationIds } =
    splitTargetElementReferenceIds(targetElements);
  const referencedDeliverableIds =
    extractImageIdsFromGraphics(viewGraphicContent);
  const objectCreationPayloads: CreateObjectPayload[] =
    getElementCreationPayloads(
      targetElements,
      fromRepoElements,
      false,
      updatedDraftObjectsPayload,
    ) as CreateObjectPayload[];
  const relationCreationPayloads: CreateRelationPayload[] =
    getElementCreationPayloads(
      targetElements,
      fromRepoElements,
      true,
    ) as CreateRelationPayload[];
  assertIsUUID(viewId);
  const viewCreationPayload: CreateViewPayload = {
    id: viewId,
    name: viewName,
    ...(viewDescription === undefined ? {} : { description: viewDescription }),
    content: { ...updatedViewGraphicContent, ...baseElements },
    kind: '', // The frontend determines the kinds it uses, e.g. 'diagram' / 'table' / 'exploration'
    definition: '', // This may contain the schemaDefinition to which the content adheres
    referencedObjectIds,
    referencedRelationIds,
    referencedDeliverableIds,
    isFreeFormat:
      referencedObjectIds.length === 0 && referencedRelationIds.length === 0,
  };
  const publishPayload: PublishViewPayload = {
    viewCreationPayload,
    objectCreationPayloads,
    relationCreationPayloads,
  };
  return publishPayload;
}

export function getElementCreationPayloads(
  semantics: PayloadDraftEntry,
  fromRepoElements: string[],
  isRelations = false,
  draftObjectAttributePayload?: DraftObjectsPayload,
): CreateObjectPayload[] | CreateRelationPayload[] {
  if (isRelations) {
    return Object.values<DraftData>(semantics)
      .filter(
        (model) =>
          !!model.sourceId &&
          relationsTypeArray.includes(model.metaModelTerm) &&
          !fromRepoElements.includes(model.id),
      )
      .map((semanticElement) =>
        generateRelationInfo(semanticElement as BasicData),
      );
  }
  return Object.values<DraftData>(semantics)
    .filter(
      (model) =>
        !model.sourceId &&
        !relationsTypeArray.includes(model.metaModelTerm) &&
        !fromRepoElements.includes(model.id),
    )
    .map((semanticElement) =>
      generateElementInfo(
        semanticElement as BasicData,
        draftObjectAttributePayload,
      ),
    );
}

//#region individual info

export function generateElementInfo(
  semanticElement: BasicData,
  draftObjectAttributePayload?: DraftObjectsPayload,
): CreateObjectPayload {
  const elementType = convertTypeNameToSdkElementType(
    semanticElement.metaModelTerm,
  );
  const semanticId = semanticElement.id;

  BDCoreMetaModelDefinition.assertIsObjectType(elementType);

  let createObjectPayload: CreateObjectPayload = {
    id: semanticId,
    labels: [], // TODO Creating element with labels is not supported yet
    name: semanticElement.name?.en ?? '',
    type: elementType,
  };

  if (draftObjectAttributePayload?.[semanticId]) {
    createObjectPayload = {
      ...createObjectPayload,
      description: draftObjectAttributePayload[semanticId].description,
      attributes:
        convertDraftAttributeCategoryPayloadToAttributeCategoryValuesPayload(
          draftObjectAttributePayload[semanticId].attributes,
        ),
    };
  }

  return createObjectPayload;
}

export function generateRelationInfo(
  semanticElement: BasicData,
): CreateRelationPayload {
  //todo: this typing of relations will fail if RelationType relies of RelationRoleName enum
  const relationRoleName = convertTypeNameToRelationRoleName(
    semanticElement.metaModelTerm,
  );
  assertIsUUID(semanticElement.sourceId);
  assertIsUUID(semanticElement.targetId);
  BDCoreMetaModelDefinition.assertIsRelationRoleName(relationRoleName);
  return {
    id: semanticElement.id,
    name: semanticElement.name?.en ?? '',
    roleName: relationRoleName,
    sourceObjectId: semanticElement.sourceId,
    targetObjectId: semanticElement.targetId,
  };
}

//#endregion
